/**
 * Admin sellers API layer (STORY-008-007). Typed calls to the SHIPPED
 * STORY-003-001 admin marketplace router (`/admin/marketplace/sellers*`) plus
 * the Track-0 admin→seller notification compose endpoint (ADR-044,
 * `/admin/sellers/{id}/notifications`). This story adds NO backend routes.
 *
 * Types mirror the backend Pydantic schemas in
 * `apps/api/garden/features/marketplace/schemas.py` (`SellerResponse`,
 * `SellerListResponse`, `SellerUpdate`, `SellerSuspendRequest`) and
 * `apps/api/garden/features/notifications/schemas.py`
 * (`AdminNotificationCreateRequest`, `NotificationResponse`). Decimal money /
 * coordinates arrive as JSON **strings** (never floats).
 */
import { apiClient, apiFetch } from "@/lib/api-client";

// ─────────────────────────── Frozen enums ──────────────────────────────────

/** Seller `verification_status` (frozen CHECK — marketplace/models.py). */
export const SELLER_VERIFICATION_STATUSES = [
  "pending",
  "verified",
  "rejected",
  "suspended",
] as const;
export type SellerVerificationStatus =
  (typeof SELLER_VERIFICATION_STATUSES)[number];

// ─────────────────────────── Types ─────────────────────────────────────────

/** One seller row (mirrors `SellerResponse`). Decimal lat/lng are strings. */
export interface AdminSeller {
  id: string;
  business_name: string;
  market_id: string;
  pavilion_number: string;
  phone_e164: string;
  entrance_hint: string | null;
  whatsapp_phone_e164: string | null;
  telegram_handle: string | null;
  lat: string | null;
  lng: string | null;
  pavilion_photo_s3_key: string | null;
  entrance_arrow_photo_s3_key: string | null;
  vacation_mode: boolean;
  verification_status: string;
  verification_notes: string | null;
}

/** Cursor-paginated list (mirrors `SellerListResponse`). */
export interface AdminSellerListResponse {
  sellers: AdminSeller[];
  next_cursor: string | null;
}

/** PATCH body (subset of `SellerUpdate` this UI edits). */
export interface AdminSellerUpdate {
  business_name?: string;
  pavilion_number?: string;
  phone_e164?: string;
  whatsapp_phone_e164?: string | null;
  telegram_handle?: string | null;
  entrance_hint?: string | null;
  verification_status?: string;
  verification_notes?: string | null;
}

/**
 * POST body (subset of `SellerCreate` this UI writes). Mirrors
 * `apps/api/garden/features/marketplace/schemas.py::SellerCreate` — the
 * router model is `extra="forbid"`, so only these keys may be sent.
 * `verification_status` is intentionally omitted: the backend default
 * (`'pending'`) is correct for an admin-created seller pending review.
 */
export interface AdminSellerCreate {
  business_name: string;
  market_id: string;
  pavilion_number: string;
  phone_e164: string;
  whatsapp_phone_e164?: string | null;
  telegram_handle?: string | null;
  entrance_hint?: string | null;
  verification_notes?: string | null;
}

/** Admin→seller notification body (mirrors `AdminNotificationCreateRequest`). */
export interface AdminNotificationBody {
  title: string;
  body: string;
  link_path?: string | null;
}

/** One notification on the wire (subset of `NotificationResponse` we use). */
export interface AdminNotificationResponse {
  id: string;
  recipient_user_id: string;
  type: string;
  title: string;
  body: string;
  created_at: string;
}

// ─────────────────────────── Query keys ────────────────────────────────────

export const sellerKeys = {
  list: (filters: Record<string, unknown>) =>
    ["admin", "sellers", filters] as const,
  detail: (id: string) => ["admin", "sellers", "detail", id] as const,
} as const;

// ─────────────────────────── Filters / fetchers ────────────────────────────

export interface ListSellersParams {
  verification_status?: string | null;
  market_id?: string | null;
  vacation_mode?: boolean | null;
  cursor?: string | null;
  limit?: number;
}

export function listSellers(
  params: ListSellersParams,
): Promise<AdminSellerListResponse> {
  const query = new URLSearchParams();
  if (params.verification_status)
    query.set("verification_status", params.verification_status);
  if (params.market_id) query.set("market_id", params.market_id);
  if (params.vacation_mode != null)
    query.set("vacation_mode", String(params.vacation_mode));
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiClient.get<AdminSellerListResponse>(
    `/admin/marketplace/sellers${qs ? `?${qs}` : ""}`,
  );
}

export function getSeller(id: string): Promise<AdminSeller> {
  return apiClient.get<AdminSeller>(`/admin/marketplace/sellers/${id}`);
}

/**
 * `POST /admin/marketplace/sellers` (AC #1 of STORY-003-001, already
 * shipped). Pass `force: true` to override a 409
 * `marketplace.seller.duplicate_pavilion` soft-uniqueness warning on
 * `(market_id, pavilion_number)`.
 */
export function createSeller(
  body: AdminSellerCreate,
  options: { force?: boolean } = {},
): Promise<AdminSeller> {
  const path = options.force
    ? "/admin/marketplace/sellers?force=true"
    : "/admin/marketplace/sellers";
  return apiClient.post<AdminSeller, AdminSellerCreate>(path, body);
}

export function patchSeller(
  id: string,
  body: AdminSellerUpdate,
): Promise<AdminSeller> {
  // `apiClient` exposes no `patch` shortcut; go through `apiFetch` directly
  // (still auto-attaches the JWT + Idempotency-Key for the mutating method).
  return apiFetch<AdminSeller, AdminSellerUpdate>({
    method: "PATCH",
    path: `/admin/marketplace/sellers/${id}`,
    body,
  });
}

export function suspendSeller(id: string, reason: string): Promise<AdminSeller> {
  return apiClient.post<AdminSeller, { reason: string }>(
    `/admin/marketplace/sellers/${id}/suspend`,
    { reason },
  );
}

export function restoreSeller(id: string): Promise<AdminSeller> {
  return apiClient.post<AdminSeller, Record<string, never>>(
    `/admin/marketplace/sellers/${id}/restore`,
    {},
  );
}

export function notifySeller(
  id: string,
  body: AdminNotificationBody,
): Promise<AdminNotificationResponse> {
  return apiClient.post<AdminNotificationResponse, AdminNotificationBody>(
    `/admin/sellers/${id}/notifications`,
    body,
  );
}
